/**
 * Contact book format: numbered lines "1. Name, email" or "1. Name – domain, email".
 * Parsed names are used when sending emails so {{first_name}} (and name) address the right contact.
 */

// Emails may contain hyphens in the local-part or domain (e.g. foo-bar@some-domain.com),
// so hyphen must NOT be in the excluded character set — only actual separators (whitespace,
// comma, em-dash) end an address.
const EMAIL_REGEX = /[^\s,–]+@[^\s,–]+/;

/**
 * Parse contact_book string into array of { name, email }.
 * Handles: "1. Name, email", "2. Name – domain, email", or plain "email@x.com".
 * The name is used when sending so each recipient gets the correct first name (e.g. "Hi Elias,").
 * @param {string} str - Raw contact_book value
 * @returns {{ name: string, email: string }[]}
 */
export function parseContactBook(str) {
  if (!str || typeof str !== 'string') return [];
  const trimmed = str.trim();
  if (!trimmed) return [];

  const lines = trimmed.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const entries = [];

  for (const line of lines) {
    const withoutNumber = line.replace(/^\d+\.\s*/, '');
    const match = withoutNumber.match(EMAIL_REGEX);
    if (match) {
      const email = match[0].trim().toLowerCase();
      let name = withoutNumber
        .replace(EMAIL_REGEX, '')
        .replace(/^[\s,–-]+|[\s,–-]+$/g, '')
        .trim();
      // If line is "Name – something, email", use only the part before " – " as the name
      if (name.includes(' – ')) {
        name = name.split(' – ')[0].trim();
      }
      entries.push({ name: name || '', email });
    }
  }

  if (entries.length > 0) return entries;

  const commaParts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  for (const part of commaParts) {
    const match = part.match(EMAIL_REGEX);
    if (match) {
      const email = match[0].trim().toLowerCase();
      const name = part.replace(EMAIL_REGEX, '').replace(/^[\s,–-]+|[\s,–-]+$/g, '').trim();
      entries.push({ name: name || '', email });
    }
  }

  return entries;
}

/**
 * Format entries into numbered contact_book string: "1. Name, email\n2. Name, email".
 * @param {{ name: string, email: string }[]} entries
 * @returns {string}
 */
export function formatContactBook(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return '';
  return entries
    .map((e, i) => {
      const name = (e.name || '').trim();
      const email = (e.email || '').trim();
      if (!email) return null;
      return `${i + 1}. ${name ? name + ', ' : ''}${email}`;
    })
    .filter(Boolean)
    .join('\n');
}

/**
 * Normalize raw contact_book string to numbered format (for display/save).
 * @param {string} str
 * @returns {string}
 */
export function normalizeContactBook(str) {
  return formatContactBook(parseContactBook(str));
}
